import { instance } from '@payments-checkout/shared/helpers/service/instance.service';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { SessionStatusResponse } from '../../types/sessionStatus.types';

interface SessionStatusWithETag {
  data: SessionStatusResponse;
  etag: string | null;
}

const NOT_MODIFIED = 304;

async function getSessionStatus(
  sessionId: string,
  previous?: SessionStatusWithETag
): Promise<SessionStatusWithETag> {
  const response = await instance.get(`/sessions/${sessionId}/resume`, {
    headers: previous?.etag ? { 'If-None-Match': previous.etag } : undefined,
    validateStatus: (status) =>
      (status >= 200 && status < 300) || status === NOT_MODIFIED,
  });

  if (response.status === NOT_MODIFIED && previous) return previous;

  return {
    data: response.data,
    etag: response.headers['etag'] || null,
  };
}

const useSessionStatus = (sessionId?: string, pollingIntervalMs?: number) => {
  const queryClient = useQueryClient();
  const queryKey = ['getSessionStatus', sessionId];

  const query = useQuery<SessionStatusWithETag, AxiosError>({
    queryKey,
    queryFn: () =>
      getSessionStatus(
        sessionId!,
        queryClient.getQueryData<SessionStatusWithETag>(queryKey)
      ),
    enabled: !!sessionId,
    refetchInterval: pollingIntervalMs ?? false,
    retry: false,
  });

  return query;
};

export { useSessionStatus, type SessionStatusWithETag };
